import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Users, Plus, Pencil, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VendorCard } from '@/components/vendor/index';
import { getCardTier } from '@/types/vendor-kpi';
import type { TeamResponse } from '@/services/teamService';
import type { User } from '@/types/auth';
import type { EnrichedMember } from '@/utils/teamFilters';
import { formatDate } from '@/utils/formatDate';

type Props = {
  team: TeamResponse;
  otherTeams: TeamResponse[];
  unassigned: User[];
  members: EnrichedMember[];
  filtersActive: boolean;
  viewMode: 'cards' | 'table';
  onSelectMember?: (userId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddMember: (userId: string) => void;
  onTransferMember: (userId: string, targetTeamId: string) => void;
  onDeleteSalesperson: (user: User) => void;
};

function initials(first?: string, last?: string) {
  return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';
}

export default function TeamCard({
  team,
  otherTeams,
  unassigned,
  members,
  filtersActive,
  viewMode,
  onSelectMember,
  onEdit,
  onDelete,
  onAddMember,
  onTransferMember,
  onDeleteSalesperson,
}: Props) {
  const { t } = useTranslation();
  const [showAssign, setShowAssign] = useState(false);

  const isFilteredEmpty = filtersActive && members.length === 0 && team.members.length > 0;

  // Cards view hides the team entirely when filters exclude all members.
  if (isFilteredEmpty && viewMode === 'cards') return null;

  return (
    <Card className="overflow-hidden py-0 gap-0">
      {/* Header */}
      <CardHeader className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
            <Users className="size-5 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">{team.name}</h2>
            <p className="text-xs text-muted-foreground">
              {filtersActive
                ? t('teamManagement.memberCountFiltered', {
                    visible: members.length,
                    total: team.members.length,
                    defaultValue: '{{visible}} de {{total}} miembros',
                  })
                : t('teamManagement.memberCount', { count: team.members.length })}
            </p>
          </div>
        </div>
        {viewMode === 'table' && (
          <div className="flex items-center gap-1.5">
            {unassigned.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setShowAssign(!showAssign)}>
                <Plus className="size-4" />
                <span className="hidden sm:inline">{t('teamManagement.assignMember')}</span>
              </Button>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onEdit}>
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('common.edit')}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onDelete} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('common.delete')}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </CardHeader>

      {/* Assign picker (table mode only) */}
      {viewMode === 'table' && showAssign && unassigned.length > 0 && (
        <div className="border-b border-border bg-muted/20 px-6 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">{t('teamManagement.selectToAssign')}</p>
          <div className="flex flex-wrap gap-2">
            {unassigned.map((sp) => (
              <Button
                key={sp.id}
                variant="outline"
                size="sm"
                onClick={() => { onAddMember(sp.id); setShowAssign(false); }}
              >
                {sp.firstName} {sp.lastName}
              </Button>
            ))}
          </div>
        </div>
      )}

      <CardContent className="p-0">
        {team.members.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            {t('teamManagement.noMembers')}
          </div>
        ) : isFilteredEmpty ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            {t('teamManagement.filters.noMatchesInTeam', 'Ningún miembro de este equipo cumple los filtros.')}
          </div>
        ) : viewMode === 'cards' ? (
          /* ── CARDS VIEW ── */
          <div className="p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {members.map((m) => {
                const tier = getCardTier(m.globalScore);
                const tierLabel = tier === 'elite' ? 'Elite' : tier === 'pro' ? 'Pro' : 'Standard';
                const tierColor = tier === 'elite' ? '#C9A84C' : tier === 'pro' ? 'var(--primary)' : undefined;

                return (
                  <VendorCard
                    key={m.user.id}
                    name={`${m.user.firstName} ${m.user.lastName}`}
                    initials={initials(m.user.firstName, m.user.lastName)}
                    globalScore={m.globalScore}
                    categories={m.profile?.categories ?? []}
                    categoryBadge={m.globalScore > 0 ? tierLabel : undefined}
                    categoryColor={tierColor}
                    totalSimulations={m.stats?.sessionCount}
                    onClick={onSelectMember ? () => onSelectMember(m.user.id) : undefined}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          /* ── TABLE VIEW ── */
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('teamManagement.tableFullName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('teamManagement.tableEmail')}
                </th>
                <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('teamManagement.tableCreatedAt')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('teamManagement.tableActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((m) => {
                const member = m.user;
                return (
                  <tr key={member.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                            {initials(member.firstName, member.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-foreground">
                          {member.firstName} {member.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-muted-foreground">{member.email}</td>
                    <td className="hidden sm:table-cell px-6 py-3 text-sm text-muted-foreground">
                      {formatDate(member.createdAt, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {otherTeams.length > 0 && (
                          <DropdownMenu modal={false}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-amber-600">
                                    <ArrowRightLeft className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                              </TooltipTrigger>
                              <TooltipContent>{t('teamManagement.transferMember', 'Transferir a otro equipo')}</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end">
                              {otherTeams.map((dest) => (
                                <DropdownMenuItem key={dest.id} onClick={() => onTransferMember(member.id, dest.id)}>
                                  {dest.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon-sm" onClick={() => onDeleteSalesperson(member)} className="text-muted-foreground hover:text-destructive">
                              <Trash2 className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t('teamManagement.deleteSalesperson')}</TooltipContent>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
